import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { usePartnerAuth } from '../../context/PartnerAuthContext';
import { usePartnerBooking } from '../../context/PartnerBookingContext';
import { getDateCards, formatCardDate, PARTNER_WINDOW_DAYS } from '../../components/shared/helpers';
import DateNoticeModal, { shouldShowDateNotice } from '../../components/shared/DateNotice';

// Partner date-selection screen. Reuses the customer SelectDate visual language
// (navy card, cormorant heading, gold date cards, Continue button) but runs on
// the isolated partner booking context and offers today + PARTNER_WINDOW_DAYS —
// partners get an early-access window (up to 9 days ahead) vs the public's 7.
// The server grants the wider window only to APPROVED partners (hold function).
export default function PartnerSelectDate() {
  const { partnerSession, partnerProfile, loadingAuth } = usePartnerAuth();
  const { selectedDate, setSelectedDate, fading, go, exitToDashboard } = usePartnerBooking();
  // One-off date notice (see components/shared/DateNotice.js) — opens once per
  // session when the keyed date is selected; never blocks continuing.
  const [noticeOpen, setNoticeOpen] = useState(false);
  const pickDate = (iso) => {
    setSelectedDate(iso);
    if (shouldShowDateNotice(iso)) setNoticeOpen(true);
  };

  // Wait for the initial auth check before deciding any redirect.
  if (loadingAuth) {
    return (
      <div className="w-full max-w-[480px] p-px rounded-2xl bg-gradient-to-b from-[rgba(212,168,67,0.32)] via-[rgba(255,255,255,0.06)] to-[rgba(255,255,255,0.02)] card-enter">
        <div className="bg-[#0D1E35] rounded-[15px] shadow-[0_40px_80px_rgba(0,0,0,0.55)] px-7 py-8">
          <p className="font-dm-sans text-sm text-white/55 font-light leading-relaxed text-center">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  // Not logged in → partner login. Logged in but not approved → dashboard, which
  // shows the partner their current status (pending / suspended / rejected).
  if (!partnerSession) return <Navigate to="/partner/login" replace />;
  if (partnerProfile?.approval_status !== 'approved') return <Navigate to="/partner" replace />;

  const dateCards = getDateCards(PARTNER_WINDOW_DAYS);

  return (
    <div className={`w-full max-w-[480px] p-px rounded-2xl bg-gradient-to-b from-[rgba(212,168,67,0.32)] via-[rgba(255,255,255,0.06)] to-[rgba(255,255,255,0.02)] card-enter transition-opacity duration-[250ms] ${fading ? 'opacity-0' : 'opacity-100'}`}>
      <div className="bg-[#0D1E35] rounded-[15px] shadow-[0_40px_80px_rgba(0,0,0,0.55)] px-7 py-8">

        {/* Exit to Dashboard — no hold on this step, so exit immediately */}
        <div className="flex justify-end mb-2">
          <button
            type="button"
            onClick={exitToDashboard}
            className="font-dm-sans text-[13px] font-medium text-[rgba(212,168,67,0.6)] hover:text-[#C9A84C] hover:underline underline-offset-2 transition-colors duration-200"
          >
            Exit to Dashboard
          </button>
        </div>

        <p className="font-dm-sans text-[12px] uppercase tracking-[0.18em] text-[rgba(201,168,76,0.85)] mb-2">
          Partner Booking
        </p>
        <h1 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-4">
          Select a Date
        </h1>

        {/* Partner early-access callout */}
        <div className="flex items-start gap-3.5 border border-[rgba(201,168,76,0.4)] rounded-lg bg-[rgba(201,168,76,0.06)] px-4 py-3 mb-4">
          <div className="w-2 h-2 rotate-45 bg-[#D4A843] shrink-0 mt-[3px]" />
          <div className="flex flex-col gap-1.5">
            <p className="font-dm-sans text-sm font-medium text-[#D4A843] leading-snug">
              Partner early access — book up to 9 days in advance
            </p>
            <p className="font-dm-sans text-sm font-light text-white/55 leading-relaxed">
              For larger group bookings, please contact{' '}
              <a href="tel:+919854958549" className="text-[#C9A84C] hover:underline underline-offset-2 transition-colors duration-200">9854958549</a>
              {' / '}
              <a href="tel:+919435558001" className="text-[#C9A84C] hover:underline underline-offset-2 transition-colors duration-200">9435558001</a>.
            </p>
          </div>
        </div>

        {/* Date cards — compact squares, four per row on every width. The
            selection is the card's ISO date. */}
        <div className="grid grid-cols-4 gap-2">
          {dateCards.map((card) => {
            const selected = selectedDate === card.iso;
            return (
              <button
                key={card.iso}
                type="button"
                onClick={() => pickDate(card.iso)}
                aria-pressed={selected}
                aria-label={card.formatted}
                className={`aspect-square w-full min-w-0 flex flex-col items-center justify-center gap-0.5 rounded-xl border transition-[border-color,background-color,color,transform] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35] ${
                  selected
                    ? 'bg-[#D4A843] border-[#D4A843] text-[#0A1628] shadow-[0_8px_24px_rgba(212,168,67,0.28)]'
                    : 'bg-[#162840] border-white/10 text-white hover:border-white/25'
                }`}
              >
                <span className={`font-dm-sans text-[9px] leading-none uppercase tracking-[0.04em] font-medium whitespace-nowrap ${selected ? 'text-[#0A1628]/70' : 'text-[rgba(212,168,67,0.85)]'}`}>
                  {card.top}
                </span>
                <span className="font-cormorant text-[28px] leading-none font-semibold lining-nums tabular-nums">
                  {card.day}
                </span>
                <span className={`font-dm-sans text-[10px] leading-none uppercase tracking-[0.12em] font-medium ${selected ? 'text-[#0A1628]/70' : 'text-white/50'}`}>
                  {card.month}
                </span>
              </button>
            );
          })}
        </div>
        <p className="mt-3 min-h-[20px] font-dm-sans text-[13px] font-light text-white/55 leading-snug" aria-live="polite">
          {selectedDate !== null ? formatCardDate(selectedDate) : 'Choose a date to continue'}
        </p>

        {/* Continue — opacity approach for clean disabled/active transition */}
        <button
          type="button"
          onClick={() => selectedDate !== null && go('/partner/book/passengers')}
          disabled={selectedDate === null}
          className={`w-full mt-4 sm:mt-6 py-[14px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-medium text-[12.5px] tracking-[0.15em] uppercase rounded-full transition-[transform,opacity] duration-[200ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none ${
            selectedDate !== null
              ? 'opacity-100 active:scale-[0.97] hover:bg-[#B8973E] focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35] cursor-pointer'
              : 'opacity-30 cursor-not-allowed'
          }`}
        >
          Continue
        </button>

      </div>

      {/* One-off date notice — acknowledge and proceed, or pick another date */}
      {noticeOpen && (
        <DateNoticeModal
          onContinue={() => { setNoticeOpen(false); go('/partner/book/passengers'); }}
          onChooseAnother={() => { setNoticeOpen(false); setSelectedDate(null); }}
        />
      )}
    </div>
  );
}
